import { useEffect, useMemo, useState } from "react";
import CalendarWidget from "../widgets/CalendarWidget";
import Rollup from "./Rollup";
import InquiryComposer from "./InquiryComposer";
import DashboardAlerts from "./DashboardAlerts";
import AppointmentSummary from "./AppointmentSummary";
import { listBuyersForSession } from "../../js/api/buyersApi";
import { listEvents } from "../../js/api/calendarApi";
import { deskTransactions } from "../../js/api/deskScope";
import { dismissDashboardAlert, listDueDashboardAlerts } from "../../js/api/dashboardAlerts";
import { loadEventDraft } from "../../js/api/eventDraft";
import { listClientDocuments } from "../../js/db/documents";
import { useAgentScope } from "../../js/hooks/useAgentScope";
import { useDesk } from "../../js/hooks/useDesk";
import { useJourneyLive } from "../../js/hooks/useJourneyLive";
import { useNotifications } from "../../js/hooks/useNotifications";
import { preferenceProgress, prettyValue } from "../../js/utils/formatters";
import { sellerProgress } from "../../js/utils/sellerSteps";
import { toTitleCase } from "../../js/utils/titleCase";
import { REPCSP_TITLE } from "../../js/utils/reptc";
import { resolveProIdentity } from "../../js/api/proIdentity";

function readSellers() {
  try {
    return JSON.parse(localStorage.getItem("dh_sellers") || "[]");
  } catch {
    return [];
  }
}

function upcomingEvents(events) {
  const today = new Date().toISOString().slice(0, 10);
  return events
    .filter((item) => item.status !== "cancelled" && String(item.date || "") >= today)
    .slice(0, 5);
}

export default function DeskDashboard({ heading = "Agent dashboard", lead, actions, heroClassName = "", badge = null }) {
  const { session, listAgentId, writeAgentId, excludeAgentId, previewing, previewId } = useAgentScope();
  const desk = useDesk();
  const { clientUnread } = useNotifications();
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedDate] = useState(new Date());
  const [pulse, setPulse] = useState(0);
  const [draft, setDraft] = useState(() => loadEventDraft(writeAgentId));
  const assigned = deskTransactions(session, listAgentId, excludeAgentId).filter(
    (item) => item.client_role !== "office"
  );
  const sellers = useMemo(() => readSellers(), [pulse]);
  const alerts = useMemo(
    () => listDueDashboardAlerts(events, session?.userId),
    [events, session, pulse]
  );
  const assignedFiles =
    assigned.length + clients.reduce((sum, client) => sum + listClientDocuments(client.id, "buyer").length, 0);

  useEffect(() => {
    listBuyersForSession(session, listAgentId, excludeAgentId).then(setClients);
    listEvents(listAgentId, excludeAgentId, { role: desk.variant === "broker" ? "broker" : "agent" }).then(setEvents);
    setDraft(loadEventDraft(writeAgentId));
  }, [session, listAgentId, excludeAgentId, writeAgentId, pulse]);

  useJourneyLive(() => setPulse((n) => n + 1));

  const sellerFiles = assigned.filter((item) => item.client_role === "seller");
  const coming = upcomingEvents(events);
  const responses = assigned.filter((item) => item.client_role === "buyer");
  const identity = resolveProIdentity(session);

  return (
    <>
      <div className={`page-header${heroClassName ? ` ${heroClassName}` : ""}`}>
        <div>
          {badge}
          <h1>{toTitleCase(heading)}</h1>
          {identity?.name ? <p className="desk-identity-name">{identity.name}</p> : null}
          <p className="reptc-line">{REPCSP_TITLE}</p>
          <p className="page-lead">
            {lead ||
              `Welcome back${identity?.name || session?.name ? `, ${identity?.name || session.name}` : ""}. Rollups on the left. Calendar stays top-right.`}
          </p>
          {previewing ? (
            <p className="muted desk-preview-bar">
              Admin preview · viewing{" "}
              {previewId === "office"
                ? "All Agents"
                : previewId === "mine"
                  ? "My Business"
                  : `agent ${previewId}`}
              . Open Admin to switch desks.
            </p>
          ) : null}
        </div>
        {actions || null}
      </div>

      <DashboardAlerts
        alerts={alerts}
        onDismiss={(id) => {
          dismissDashboardAlert(session?.userId, id);
          setPulse((n) => n + 1);
        }}
      />

      <div className="agent-desk" data-tour="agent-desk">
        <div className="agent-desk__left">
          <Rollup title="Checklist Rollup" badge={clients.length || null} defaultOpen to={desk.clients} tour="desk-rollup">
            {clients.length ? (
              <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                {clients.map((client) => {
                  const progress = preferenceProgress(client.preferences);
                  return (
                    <li key={client.id}>
                      <strong>{client.name}</strong>
                      <div className="muted">
                        Buyer · {progress.percent}% · {client.status || "active"}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted">No buyer checklists assigned yet.</p>
            )}
            {sellerFiles.length ? (
              <ul className="stack" style={{ listStyle: "none", padding: 0, marginTop: "0.85rem" }}>
                {sellerFiles.map((item) => {
                  const seller =
                    sellers.find((row) => row.id === item.client_id) ||
                    sellers.find((row) => row.email === item.client?.email);
                  const progress = seller ? sellerProgress(seller.portfolio) : null;
                  return (
                    <li key={item.id}>
                      <strong>{item.client?.name || item.property_address || "Seller file"}</strong>
                      <div className="muted">
                        Seller
                        {progress ? ` · ${progress.percent}%` : " · portfolio on the seller path"}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </Rollup>

          <Rollup title="Files" badge={assigned.length || null} to={desk.files} tour="desk-files">
            {assigned.length ? (
              <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                {assigned.slice(0, 5).map((item) => (
                  <li key={item.id}>
                    <strong>{item.client?.name || item.property_address || "File"}</strong>
                    <div className="muted">
                      {prettyValue(item.client_role)} · {prettyValue(item.status)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Open Files to search the full packet index.</p>
            )}
          </Rollup>

          <Rollup title="Assigned Transactions" badge={assigned.length || null} to={desk.messages}>
            {assigned.length ? (
              <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                {assigned.map((item) => (
                  <li key={item.id}>
                    <strong>{item.property_address || item.id}</strong>
                    <div className="muted">
                      {prettyValue(item.status)} · {prettyValue(item.client_role)} ·{" "}
                      {item.client?.name || item.client_id}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No messaging transactions are assigned to this desk.</p>
            )}
          </Rollup>

          <Rollup title="Inquiry/Response" badge={responses.length || null} to={desk.responses}>
            <p className="muted">Record how a listing fits a buyer’s DreamHome Checklist.</p>
          </Rollup>

          <Rollup title="Upcoming Tasks" badge={coming.length || null} to={desk.calendar}>
            {coming.length ? (
              <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                {coming.map((item) => (
                  <li key={item.id}>
                    <strong>{item.title}</strong>
                    <div className="muted">
                      {item.date}
                      {item.time ? ` · ${item.time}` : ""}
                      {item.type ? ` · ${prettyValue(item.type)}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Nothing scheduled ahead.</p>
            )}
          </Rollup>

          <Rollup title="Office inquiry" to={desk.officeMessages}>
            <InquiryComposer desk={desk.variant === "broker" ? "broker" : "agent"} />
          </Rollup>
        </div>

        <div className="agent-desk__right desk-calendar-stack" data-tour="desk-calendar">
          <CalendarWidget events={events} selectedDate={selectedDate} compact indicatorsOnly />
          <AppointmentSummary draft={draft} events={events} />
          <div className="agent-stat-row">
            <article className="card agent-stat">
              <p className="stat-label">{toTitleCase("Active Buyers")}</p>
              <p className="stat-value">{clients.length}</p>
            </article>
            <article className="card agent-stat">
              <p className="stat-label">Unread Notes</p>
              <p className="stat-value">{clientUnread}</p>
            </article>
            <article className="card agent-stat">
              <p className="stat-label">Assigned Files</p>
              <p className="stat-value">{assignedFiles}</p>
            </article>
          </div>
        </div>
      </div>
    </>
  );
}
